/**
 * Showcase Slice
 *
 * Grid layout with 4 items:
 * - 1 Full width (top row)
 * - 1 Half width (bottom left)
 * - 2 Quarter width (stacked, bottom right)
 *
 * Each item has: poster image, hover video, title, subtitle
 */

import React, { useRef, useState, useEffect, useMemo } from "react";
import { graphql } from "gatsby";
import { Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { motion } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";
import BackgroundStaticVideo from "../../components/BackgroundStaticVideo";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import * as styles from "./Showcase.module.scss";

// Compute proper offset for Swiper slides to start and end within the page width
const getSlidesOffset = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }

  // Grab the page width from the first main element
  const pageWidthElement = document.querySelector("main .page-width");
  if (!pageWidthElement) {
    return 0;
  }

  const pageWidthStyle =
    getComputedStyle(pageWidthElement).getPropertyValue("max-width");
  const pageMarginStyle =
    getComputedStyle(pageWidthElement).getPropertyValue("padding-left");
  const pageWidth = parseInt(pageWidthStyle, 10) || 0;
  const pageMargin = parseInt(pageMarginStyle, 10) || 0;

  if (!pageWidth) {
    return pageMargin;
  }

  const slidesOffset =
    window.innerWidth > pageWidth
      ? (window.innerWidth - pageWidth) / 2 + pageMargin
      : pageMargin;

  return slidesOffset;
};

const updateSwiperOffsets = (swiper) => {
  if (!swiper || !swiper.params) return;

  const offset = getSlidesOffset();

  swiper.params.slidesOffsetBefore = offset;
  swiper.params.slidesOffsetAfter = offset;

  if (typeof swiper.update === "function") {
    swiper.update();
  }
};

const handleSwiperInit = (swiper) => {
  updateSwiperOffsets(swiper);

  if (swiper && swiper.el && swiper.el.dataset) {
    swiper.el.dataset.initialized = "true";
  }
};

const ShowcaseItem = ({ item }) => {
  const videoRef = useRef(null);

  const posterUrl = item.poster_image?.url;
  const videoUrl = item.hover_video_url;
  const title = item.title;
  const subtitle = item.subtitle;
  const link = item.link;
  const hasVideo = Boolean(videoUrl);

  const handleMouseEnter = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay may be blocked, ignore error
      });
    }
  };

  const handleMouseLeave = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <article
      className={styles.item}
      data-has-video={hasVideo ? "true" : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <div className={styles.mediaWrapper}>
        <div className={styles.media}>
          {posterUrl && (
            <img
              src={posterUrl}
              alt={item.poster_image?.alt || title || ""}
              className={styles.poster}
              loading="lazy"
            />
          )}
          {hasVideo && (
            <video
              ref={videoRef}
              src={videoUrl}
              className={styles.video}
              muted
              loop
              playsInline
              preload="metadata"
            />
          )}
        </div>

        {link?.url && (
          <a
            href={link.url}
            target={link.target || undefined}
            rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
            className={styles.link}
            aria-label={title || "View showcase item"}
          />
        )}
      </div>

      {(title || subtitle) && (
        <div className={styles.content}>
          <span className={styles.indicator} aria-hidden="true" />
          <div className={styles.text}>
            {title && <h3 className={styles.title}>{title}</h3>}
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>
        </div>
      )}
    </article>
  );
};

export const Showcase = ({ slice }) => {
  const [activeCategory, setActiveCategory] = useState(null);

  const { color_scheme, show_background_static_video, heading } = slice?.primary || {};
  const items = slice?.items || [];

  // Get all unique categories from the items
  const categories = items.map((item) => item.category).filter(Boolean);
  const uniqueCategories = useMemo(
    () => [...new Set(categories)],
    [categories]
  );

  // Set the first category as active if no category is selected
  useEffect(() => {
    if (!uniqueCategories.length) {
      setActiveCategory(null);
      return;
    }
    setActiveCategory((prev) =>
      prev && uniqueCategories.includes(prev) ? prev : uniqueCategories[0]
    );
  }, [uniqueCategories]);

  // Group items by category
  const itemsByCategory = uniqueCategories.reduce((acc, category) => {
    acc[category] = items.filter((item) => item.category === category);
    return acc;
  }, {});

  if (!uniqueCategories.length) {
    return null;
  }

  const tabContainerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.1, 0.25, 1],
        staggerChildren: 0.05,
      },
    },
  };

  const tabItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <section
      className={styles.showcase}
      data-color-scheme={color_scheme || "black"}
      data-background-static-video={
        show_background_static_video ? "true" : undefined
      }
      id="showcase"
    >
      {show_background_static_video && <BackgroundStaticVideo />}
      <div className={`padding-section-vertical page-width ${styles.tabs}`}>
        {heading?.richText && (
          <motion.div
            className={styles.heading}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <PrismicRichText field={heading.richText} components={{
              heading2: ({ children }) => (
                <h2 className="h1 mb-05">{children}</h2>
              ),
            }} />
          </motion.div>
        )}
        <div className={styles.tabsList}>
          {uniqueCategories.map((category) => (
            <Button
              key={category}
              className={styles.tabButton}
              type="button"
              data-active={activeCategory === category ? "true" : "false"}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>
      <div
        className={`padding-section-vertical pt-0 ${styles.tabsContent}`}
      >
        {uniqueCategories.map((category) => (
          <motion.div
            key={category}
            variants={tabContainerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className={styles.tabContent}
            data-active={activeCategory === category ? "true" : "false"}
          >
            <Swiper
              modules={[Autoplay, Navigation]}
              slidesPerView="auto"
              autoplay={itemsByCategory[category].length > 1 ? { delay: 5000 } : false}
              slidesPerGroup={1}
              rewind={true}
              spaceBetween={16}
              navigation={itemsByCategory[category].length > 1 ? {
                  prevEl: ".swiper-button-prev",
                  nextEl: ".swiper-button-next",
                }
              : false}
              className={styles.swiper}
              onInit={handleSwiperInit}
              onResize={updateSwiperOffsets}
            >
              {itemsByCategory[category].map((item, index) => (
                <SwiperSlide key={index} className={styles.slide}>
                  <motion.div
                    variants={tabItemVariants}
                  >
                    <ShowcaseItem item={item} />
                  </motion.div>
                </SwiperSlide>
              ))}
              {itemsByCategory[category].length > 1 && (<>
                <button aria-label="Previous" className={`${styles.navButton} swiper-button-prev`} data-direction="previous">
                  <Icon name="chevronLeft" size="2.8rem" />
                </button>
                <button aria-label="Next" className={`${styles.navButton} swiper-button-next`} data-direction="next">
                  <Icon name="chevronRight" size="2.8rem" />
                </button>
              </>)}
            </Swiper>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export const query = graphql`
  fragment HomepageDataBodyShowcase on PrismicHomepageDataBodyShowcase {
    primary {
      heading {
        richText
        text
      }
      color_scheme
      show_background_static_video
    }
    items {
      title
      subtitle
      category
      poster_image {
        url
        alt
      }
      hover_video_url
      link {
        url
        target
        link_type
      }
    }
  }
`;
